import i18n from '../i18n'
import { faDigits } from './numerals'

/**
 * The sign-in screen's rules (design page 2, `login`; P-login of the
 * go-live plan). The code length is not this client's to choose: it is
 * the backend's `OTP_LENGTH` (`backend-nest/src/auth/auth.constants.ts`),
 * which the Android app pins in CI and `lib/otp.test.js` pins here — the
 * design canvas once drew five cells, and five is exactly the drift that
 * check exists to catch (`api-contract.md` §4).
 */
export const OTP_LENGTH = 6

const PERSIAN = '۰۱۲۳۴۵۶۷۸۹'
const ARABIC = '٠١٢٣٤٥٦٧٨٩'

/**
 * Only digits, as Latin — a code pasted from an SMS or typed on a Persian
 * keyboard arrives in Persian or Arabic-Indic digits and must still be
 * the code.
 */
export function digitsOnly(text) {
  return String(text ?? '')
    .replace(/[۰-۹]/g, (digit) => String(PERSIAN.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String(ARABIC.indexOf(digit)))
    .replace(/\D/g, '')
}

/**
 * A phone number typed free-form (the admin panel has no country picker),
 * as the E.164 string the backend expects (V1-p). Persian and Arabic-Indic
 * digits become Latin, spaces and dashes go, a leading `00` becomes `+`,
 * and a bare Iranian mobile (`0912…`) gets `+98` — the same rule
 * `toE164(98, …)` applies on the learner screen. Anything already starting
 * with `+` is kept as typed apart from the digit conversion.
 */
export function normalizePhone(text) {
  const raw = String(text ?? '').trim()
  const plus = raw.startsWith('+') || raw.startsWith('00')
  const digits = digitsOnly(raw)
  if (!digits) return ''
  if (plus) return `+${digits.replace(/^00/, '')}`
  if (digits.startsWith('0')) return `+98${digits.replace(/^0+/, '')}`
  return `+${digits}`
}

/** A typed code, cut to the length the backend will read. */
export function normalizeCode(text) {
  return digitsOnly(text).slice(0, OTP_LENGTH)
}

export function codeComplete(code) {
  return code.length === OTP_LENGTH
}

/** The resend countdown as ۰۰:۴۲ — a count, not a time code, so Persian digits (design-system.md). */
export function faCountdown(totalSeconds) {
  return faDigits(countdown(totalSeconds))
}

function countdown(totalSeconds) {
  const whole = Math.max(0, Math.floor(Number(totalSeconds) || 0))
  const pad2 = (value) => String(value).padStart(2, '0')
  return `${pad2(Math.floor(whole / 60))}:${pad2(whole % 60)}`
}

/** The countdown in the page's language: Persian digits in fa, Latin otherwise. */
export function countdownLabel(totalSeconds, language = i18n.language) {
  return language === 'fa' ? faCountdown(totalSeconds) : countdown(totalSeconds)
}

const t = (key, options) => i18n.t(key, { ns: 'auth', ...options })

/**
 * The verify button's label (`docs/screens.md` §1): disabled with «کد را
 * کامل کن» until the code is complete, «تأیید و ورود» then, and the
 * waiting sentence while the server checks it — each in the page's
 * language (the `auth` namespace; the site is fully Persian or fully
 * English, owner's rule 2026-09-19).
 */
export function verifyLabel(code, busy) {
  if (busy) return t('verifying')
  return codeComplete(code) ? t('verifyReady') : t('verifyIncomplete')
}

/** «شش» / "six" — the code length as a word, from the contract's number. */
export function codeLengthWord() {
  return t(`digits.${OTP_LENGTH}`, { defaultValue: String(OTP_LENGTH) })
}

/** The phone field's placeholder per country, the Android app's samples; a row's own `phoneHint` from the API wins. */
export const PHONE_HINTS = { IR: '912 345 6789', DE: '1511 234 5678', OM: '9123 4567' }

export function phoneHint(country) {
  if (typeof country === 'string') return PHONE_HINTS[country] ?? ''
  return country?.phoneHint || PHONE_HINTS[country?.code] || ''
}
